import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { DateTime as LuxonDateTime } from 'luxon';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Schedule } from 'src/providers/schedule/schedule';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';

import { Loading } from 'src/shared/components/loading/loading';

import { ToDoListRoomModel } from 'src/models/to-do.model';

@Component({
    templateUrl: 'apply-to-jobs.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ToDosApplyToJobsPage implements OnInit {

  addresses: any[] = [];
  addressID: any;
  addressMap: Map<number, any> = new Map();
  hasTwoOrMoreLists: boolean;
  jobs: any[];
  isRightSideContent: boolean;
  listIsDefault: boolean;
  listName: string;
  loaded: boolean;
  notSelectedJobs = [];
  needsNewDefault: boolean;
  numberJobsToShow = 5;
  selectedAddressId: any;
  selectedJobs = [];
  startedAsDefault: boolean;
  taskListId: number | null = null;
  toDoLists: ToDoListRoomModel[];
  boundSaveChanges = this.saveChanges.bind(this);
  /** Job IDs that already use this list (from task-lists applied-jobs API). Source of truth when set. */
  appliedJobIds = new Set<number>();

constructor(
    private client: Client,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private schedule: Schedule,
    private storage: TidyStorage,
    private toDos: ToDos
  ) {}

  @Loading('', true)
  async ngOnInit() {
    try {
      this.loaded = false;
      await this.getParams();
      await this.getJobs();
      this.loaded = true;
    } catch (_err) {
      // Silent error handling
    }
  }

  async getParams() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const dialogParams = await this.storage.retrieve('dialog-params');
    this.rightSidePanelService.setDialogPageTitle('Apply List to Jobs');
    this.listName = this.navCtrl.getParam('listName') || dialogParams.listName;
    this.listIsDefault = this.navCtrl.getParam('listIsDefault') || dialogParams.listIsDefault;
    this.startedAsDefault = this.listIsDefault;
    this.hasTwoOrMoreLists = this.navCtrl.getParam('hasTwoOrMoreLists') || dialogParams.hasTwoOrMoreLists;
    this.toDoLists = this.navCtrl.getParam('toDoLists') || dialogParams.toDoLists;
    this.addressID = parseInt(localStorage.getItem('addressId')) || dialogParams.addressId;
    const storedTaskListId = localStorage.getItem('taskListId');
    this.taskListId = storedTaskListId ? parseInt(storedTaskListId, 10) : null;
  }

  showJob(i) {
    if (i < this.numberJobsToShow) {
      return true;
    }
  }

  showMoreJobs() {
    this.numberJobsToShow = 100;
  }

  async getJobs() {
    this.addresses = await this.client.getAddresses();
    this.addressMap = new Map(this.addresses.map(addr => [addr.id, addr]));

    this.appliedJobIds = new Set<number>();
    if (this.taskListId !== null) {
      try {
        const applied = await this.toDos.getAppliedJobs(this.taskListId);
        this.appliedJobIds = new Set((applied || []).map((j: { id: number }) => j.id));
      } catch (_err) {
        // Fall back to calendar task_list when applied-jobs fails
      }
    }

    const today = LuxonDateTime.now();
    const startDate = today.toISODate();
    const endDate = today.plus({ days: 13 }).toISODate();

    const filters = {};
    const scheduleData = await this.schedule.getScheduleCalendar(
      startDate,
      endDate,
      filters,
      'jobs'
    );

    this.jobs = this.parseCalendarJobs(scheduleData);
    this.parseSelectedJobs();
  }

  parseCalendarJobs(scheduleData: any[]): any[] {
    const allJobs = [];

    scheduleData?.forEach((day) => {
      day?.jobs?.forEach((job) => {
        const address = this.addressMap.get(job.address_id);

        const taskList = job.task_list || job.address_task_list || null;
        const fromApi = this.appliedJobIds.has(job.id);
        const fromCalendar =
          taskList &&
          (taskList.title === this.listName ||
            (this.taskListId !== null && taskList.id !== null && Number(taskList.id) === Number(this.taskListId)));
        const usesThisList = fromApi || !!fromCalendar;
        const transformedJob = {
          template: 'cleaning',
          template_data: {
            job: {
              id: job.id,
              service_type_details: {
                name: job.service_name || job.service_category
              },
              start_datetime_local: this.buildStartDateTime(day.date, job.start_time, job.timezone),
              task_list: taskList
            }
          },
          addressName: this.getPropertyName(address),
          usesThisList
        };

        allJobs.push(transformedJob);
      });
    });

    allJobs.sort((a, b) => {
      const dateA = new Date(a.template_data.job.start_datetime_local);
      const dateB = new Date(b.template_data.job.start_datetime_local);
      return dateA.getTime() - dateB.getTime();
    });

    return allJobs;
  }

  buildStartDateTime(date: string, startTime: string, timezone: string): string {
    const parsedDate = LuxonDateTime.fromFormat(date, 'MMM d, yyyy', { zone: timezone });
    const [hours, minutes] = (startTime || '00:00').split(':').map(Number);
    return parsedDate.set({ hour: hours, minute: minutes }).toISO();
  }

  getPropertyName(address: any): string {
    if (!address) {return '';}
    if (address.address_name) {
      return address.address_name;
    }
    if (address.address2 && address.address2 !== '') {
      return `${address.address1}, ${address.address2}`;
    }
    return address.address1 || '';
  }

  selectJob(jobId) {
    let jobAlreadySelected = false;
    this.selectedJobs.find((job) => {
      if (jobId === job) {
        jobAlreadySelected = true;
      };
    });
    if (!jobAlreadySelected) {
      this.selectedJobs.push(jobId);
      this.notSelectedJobs = this.notSelectedJobs.filter((job) => {
        if (jobId !== job) {
          return job;
        };
      });
    } else {
      this.notSelectedJobs.push(jobId);
      this.selectedJobs = this.selectedJobs.filter((job) => {
        if (jobId !== job) {
          return job;
        };
      });
    };
  }

  async saveChanges() {
    this.storage.delete('addresses');
    const taskListId = localStorage.getItem('taskListId');
    if (this.selectedJobs.length > 0) {
      await this.toDos.assignListToJob(this.selectedJobs, taskListId);
    }
    if (this.notSelectedJobs.length > 0) {
      await this.toDos.assignListToJob(this.notSelectedJobs, null);
    }
    if (this.hasTwoOrMoreLists) {
      if (this.startedAsDefault && !this.listIsDefault) {
        const params = {
          toDoLists: this.toDoLists,
          defaultListId: this.toDoLists[0].id,
          cameFromApplyListToJobs: true
        }
        return this.rightSidePanelService.navigateTo('select-default-list', params);
      } else if (!this.startedAsDefault && this.listIsDefault) {
        const payload = {
          address_task_list_id: taskListId,
          address_id: this.addressID,
          settings: [{
            key: `default_list.address.${this.addressID}`,
            value: true
          }]
        }
        await this.toDos.saveDefaultList(taskListId, payload);
      }
    }
    this.rightSidePanelService.navigateTo('to-dos');
  }

  changeDefault() {
    this.listIsDefault = !this.listIsDefault;
  }

  jobUsesThisList(job: any): boolean {
    const jobId = job?.template_data?.job?.id;
    if (jobId !== null && jobId !== undefined && this.appliedJobIds.has(jobId)) {
      return true;
    }
    const taskList = job?.template_data?.job?.task_list;
    if (!taskList) {
      return false;
    }
    const titleMatches = taskList.title === this.listName;
    const idMatches = this.taskListId !== null && taskList.id !== null && Number(taskList.id) === Number(this.taskListId);
    return titleMatches || idMatches;
  }

  parseSelectedJobs() {
    this.jobs.map((job) => {
      if (this.jobUsesThisList(job)) {
        this.selectedJobs.push(job.template_data.job.id);
      } else {
        this.notSelectedJobs.push(job.template_data.job.id);
      }
    });
  }


}
